import React, { useState, useEffect } from "react";
import {
  fetchMeadiaSourceData,
  updateSubMediaSource,
  fetchUserData,
  assignTargetToUsers,
  fetchAllAssigedObjects,
} from "../Services/APIService";
import { Toaster, toast } from "react-hot-toast";
import Select from "react-select";
import { MultiValue, ActionMeta } from "react-select";
import { useNavigate } from "react-router-dom";

interface MediaSource {
  sourceId: number;
  sourceName: string;
  isActive: boolean;
  createdBy: number;
  createdOn: string;
}

interface SubMediaSource {
  id: number;
  name: string;
  mediaSourceID: number;
  mediaSourceName: string;
  isActive: boolean;
  createdBy: number;
  createdOn: Date;
  isAssignTabShown?: boolean;
}

interface FormData {
  id: number;
  name: string;
  mediaSourceID: number;
  isActive: boolean;
}

interface ValidationErrors {
  name?: string;
  mediaSourceID?: string;
  listOfUsers?: string;
}

interface SubMediaSourceEditProps {
  initialSubMediaSource: SubMediaSource;
  onEditComplete: () => void;
  onCancel: () => void;
}

interface User {
  userId: number;
  userName: string;
  email: string;
}

interface SelectOption {
  value: number;
  label: string;
  userData: User;
}

interface AssignmentUser {
  userId: number;
  isActive: boolean;
}

interface AssignmentData {
  tenantId: number;
  assignmentTypeId: number;
  assignmentObjectId: number;
  isAssigned: boolean;
  listOfUsers: AssignmentUser[];
  createdBy: number;
}

interface APISubmitUser {
  userId: number;
}

interface APISubmitData {
  tenantId: number;
  assignmentTypeId: number;
  assignmentObjectId: number;
  isAssigned: boolean;
  listOfUsers: APISubmitUser[];
  createdBy: number;
}

const SubMediaSourceEdit: React.FC<SubMediaSourceEditProps> = ({
  initialSubMediaSource,
  onEditComplete,
  onCancel,
}) => {
  const [subMediaSourceData, setSubMediaSourceData] =
    useState<SubMediaSource | null>(null);
  const [formData, setFormData] = useState<FormData>({
    id: 0,
    name: "",
    mediaSourceID: 0,
    isActive: true,
  });

  const [mediaSources, setMediaSources] = useState<MediaSource[]>([]);
  const [isLoadingMediaSources, setIsLoadingMediaSources] = useState(true);

  const [activeTab, setActiveTab] = useState("tab1");
  const [errors, setErrors] = useState<ValidationErrors>({});

  const [users, setUsers] = useState<User[]>([]);
  const [userOptions, setUserOptions] = useState<SelectOption[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<SelectOption[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isSubmittingAssignment, setIsSubmittingAssignment] = useState(false);
  const [isLoadingAssignedUsers, setIsLoadingAssignedUsers] = useState(false);
  const [initialAssignedUsers, setInitialAssignedUsers] = useState<number[]>(
    []
  );

  const navigate = useNavigate();

  const [assignmentData, setAssignmentData] = useState<AssignmentData>({
    tenantId: 0,
    assignmentTypeId: 4,
    assignmentObjectId: initialSubMediaSource?.id || 0,
    isAssigned: true,
    listOfUsers: [],
    createdBy: 0,
  });

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
  };

  // Fetch Media Sources, Users, and Assigned Users
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingMediaSources(true);
      setIsLoadingUsers(true);
      setIsLoadingAssignedUsers(true);
      try {
        const [mediaSourcesData, usersData, assignedUsersData] =
          await Promise.all([
            fetchMeadiaSourceData(),
            fetchUserData(),
            fetchAllAssigedObjects(initialSubMediaSource.id, 4),
          ]);
        setMediaSources(mediaSourcesData);
        setActiveTab(initialSubMediaSource.isAssignTabShown ? "tab2" : "tab1");
        // Format users as select options
        setUsers(usersData);
        const options = usersData.map((user) => ({
          value: user.userId,
          label: `${user.userName} (${user.email})`,
          userData: user,
        }));
        setUserOptions(options);

        // Pre-select users in the dropdown based on assigned users
        const preSelected = options.filter((option) =>
          assignedUsersData.some(
            (assignedUser: { userId: number }) =>
              assignedUser.userId === option.value
          )
        );
        setSelectedUsers(preSelected);

        // Store initial assigned user IDs for comparison
        const initialUserIds = assignedUsersData.map(
          (user: { userId: number }) => user.userId
        );
        setInitialAssignedUsers(initialUserIds);

        // Update assignmentData with pre-selected users
        setAssignmentData((prev) => ({
          ...prev,
          listOfUsers: preSelected.map((option) => ({
            userId: option.value,
            isActive: true,
          })),
        }));
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load required data");
      } finally {
        setIsLoadingMediaSources(false);
        setIsLoadingUsers(false);
        setIsLoadingAssignedUsers(false);
      }
    };

    fetchData();
    console.log("Initial SubMediaSource:", initialSubMediaSource);
  }, [initialSubMediaSource.id]);

  useEffect(() => {
    if (initialSubMediaSource) {
      setSubMediaSourceData(initialSubMediaSource);
      setFormData({
        id: initialSubMediaSource.id,
        name: initialSubMediaSource.name,
        mediaSourceID: initialSubMediaSource.mediaSourceID,
        isActive: initialSubMediaSource.isActive,
      });
      setAssignmentData((prev) => ({
        ...prev,
        assignmentObjectId: initialSubMediaSource.id,
      }));
    }
  }, [initialSubMediaSource]);

  useEffect(() => {
    const tenantId = localStorage.getItem("tenantId");
    const userId = localStorage.getItem("UserId");

    setAssignmentData((prev) => ({
      ...prev,
      tenantId: tenantId ? parseInt(tenantId) : 0,
      createdBy: userId ? parseInt(userId) : 0,
    }));
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "mediaSourceID" ? parseInt(value) : value,
    }));

    if (errors[name as keyof ValidationErrors]) {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleMultiSelectChange = (
    newValue: MultiValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>
  ) => {
    const selectedOptions = Array.isArray(newValue) ? newValue : [];
    setSelectedUsers(selectedOptions);

    // Update assignmentData.listOfUsers with currently selected users
    const updatedListOfUsers: AssignmentUser[] = selectedOptions.map(
      (option) => ({
        userId: option.value,
        isActive: true,
      })
    );

    setAssignmentData({
      ...assignmentData,
      listOfUsers: updatedListOfUsers,
    });

    // Clear validation error if at least one user is selected
    if (selectedOptions.length > 0 && errors.listOfUsers) {
      setErrors({
        ...errors,
        listOfUsers: undefined,
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = "Sub Media Source Name is required";
      isValid = false;
    }
    // if (!formData.mediaSourceID) {

    //   newErrors.mediaSourceID = "Please select a Media Source";
    //   isValid = false;
    // }
    const activeMediaSourceIds = mediaSources
      .filter((source) => source.isActive)
      .map((source) => source.sourceId);

    if (
      !formData.mediaSourceID ||
      !activeMediaSourceIds.includes(formData.mediaSourceID)
    ) {
      newErrors.mediaSourceID = "Please select a Media Source";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateAssignmentData = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!selectedUsers || selectedUsers.length === 0) {
      newErrors.listOfUsers = "Please select at least one user";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      //toast.error("Please fix the errors in the form");
      return;
    }

    try {
      const userId = localStorage.getItem("UserId");
      if (!userId) {
        throw new Error("User ID not found in localStorage");
      }

      const payload = {
        id: formData.id,
        name: formData.name.trim().replace(/\s+/g, " "),
        mediaSourceID: formData.mediaSourceID,
        isActive: formData.isActive,
        modifieddBy: parseInt(userId),
      };

      const result = await updateSubMediaSource(payload);

      if (result.isUpdated) {
        toast.success("Sub Media Source Updated Successfully!");
        setTimeout(() => {
          onEditComplete();
          navigate("/subMediaSource");
        }, 1000);
      } else {
        throw new Error(result.message || "Failed to update Sub Media Source");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleAssignedSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateAssignmentData()) {
      toast.error("Please select at least one user.");
      return;
    }

    try {
      setIsSubmittingAssignment(true);
      setIsLoadingAssignedUsers(true);

      const currentSelectedUserIds = selectedUsers.map((user) => user.value);

      const usersToUnassign = initialAssignedUsers.filter(
        (userId) => !currentSelectedUserIds.includes(userId)
      );

      const usersToAssign = currentSelectedUserIds;

      // Perform unassign operation if there are users to unassign
      if (usersToUnassign.length > 0) {
        const unassignData: APISubmitData = {
          tenantId: assignmentData.tenantId,
          assignmentTypeId: assignmentData.assignmentTypeId,
          assignmentObjectId: assignmentData.assignmentObjectId,
          isAssigned: false,
          listOfUsers: usersToUnassign.map((userId) => ({ userId })),
          createdBy: assignmentData.createdBy,
        };

        const unassignResult = await assignTargetToUsers(unassignData);
        if (!unassignResult.isUpdated) {
          throw new Error(
            unassignResult.message || "Failed to unassign users."
          );
        }
      }

      // Perform assign operation if there are users to assign
      if (usersToAssign.length > 0) {
        const assignData: APISubmitData = {
          tenantId: assignmentData.tenantId,
          assignmentTypeId: assignmentData.assignmentTypeId,
          assignmentObjectId: assignmentData.assignmentObjectId,
          isAssigned: true,
          listOfUsers: usersToAssign.map((userId) => ({ userId })),
          createdBy: assignmentData.createdBy,
        };

        const assignResult = await assignTargetToUsers(assignData);
        if (!assignResult.isUpdated) {
          throw new Error(assignResult.message || "Failed to assign users.");
        }

        // Show success message using result.message
        toast.success(assignResult.message || "Users updated successfully");
      }

      if (usersToUnassign.length === 0 && usersToAssign.length === 0) {
        toast.error("No users selected to assign.");
        onEditComplete();
        navigate("/subMediaSource");
        return;
      }

      // Re-fetch all users to ensure userOptions is up-to-date
      const updatedUsersData = await fetchUserData();
      setUsers(updatedUsersData);
      const updatedOptions = updatedUsersData.map((user) => ({
        value: user.userId,
        label: `${user.userName} (${user.email})`,
        userData: user,
      }));
      setUserOptions(updatedOptions);

      // Re-fetch assigned users after successful assignment
      const assignedUsersData = await fetchAllAssigedObjects(
        4,
        initialSubMediaSource.id
      );
      const preSelected = updatedOptions.filter((option) =>
        assignedUsersData.some(
          (assignedUser: { userId: number }) =>
            assignedUser.userId === option.value
        )
      );
      setSelectedUsers(preSelected);

      // Update initialAssignedUsers after submission
      const newInitialUserIds = assignedUsersData.map(
        (user: { userId: number }) => user.userId
      );
      setInitialAssignedUsers(newInitialUserIds);

      // Update assignmentData with pre-selected users
      setAssignmentData((prev) => ({
        ...prev,
        listOfUsers: preSelected.map((option) => ({
          userId: option.value,
          isActive: true,
        })),
      }));

      setTimeout(() => {
        onEditComplete();
        navigate("/subMediaSource");
      }, 1000);
    } catch (error) {
      console.error("Error assigning users:", error);
      toast.error("An error occurred while assigning users");
    } finally {
      setIsSubmittingAssignment(false);
      setIsLoadingAssignedUsers(false);
    }
  };

  const handleCancel = () => {
    onEditComplete();
    onCancel();
  };

  if (!subMediaSourceData) {
    return <div>Loading...</div>;
  }

  const requiredField = <span className="text-danger ms-1">*</span>;

  return (
    <div className="card">
      <ul
        className="nav cardHeaderHegiht tabHeaderPadding nav-tabs"
        id="myTab"
        role="tablist"
        style={{ borderBottom: "1px solid #84556b " }}
      >
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link card-title cardHeaderHegiht tab-btn ${
              activeTab === "tab1" ? "active" : ""
            }`}
            id="tab1-tab"
            onClick={() => handleTabClick("tab1")}
            type="button"
            role="tab"
            aria-controls="tab1-tab-pane"
            aria-selected={activeTab === "tab1"}
          >
            Sub Media Source Details
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link card-title cardHeaderHegiht tab-btn ${
              activeTab === "tab2" ? "active" : ""
            }`}
            id="tab2-tab"
            onClick={() => handleTabClick("tab2")}
            type="button"
            role="tab"
            aria-controls="tab2-tab-pane"
            aria-selected={activeTab === "tab2"}
          >
            Assigner Information
          </button>
        </li>
      </ul>

      <div className="tab-content card-body" id="myTabContent">
        <div
          className={`tab-pane fade ${
            activeTab === "tab1" ? "show active" : ""
          }`}
          id="tab1-tab-pane"
          role="tabpanel"
          aria-labelledby="tab1-tab"
          tabIndex={0}
        >
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-12 col-lg-6 mb-3">
                <label htmlFor="id" className="form-label">
                  Sub Media Source ID
                </label>
                <input
                  type="number"
                  id="id"
                  name="id"
                  className="form-control form-control-lg"
                  value={formData.id}
                  disabled
                  readOnly
                />
              </div>

              <div className="col-12 col-lg-6 mb-3">
                <label htmlFor="mediaSourceID" className="form-label">
                  Media Source Name{requiredField}
                </label>
                {isLoadingMediaSources ? (
                  <div className="text-muted">Loading Media Sources...</div>
                ) : (
                  <select
                    id="mediaSourceID"
                    name="mediaSourceID"
                    className={`form-select form-control-lg ${
                      errors.mediaSourceID ? "is-invalid" : ""
                    }`}
                    value={formData.mediaSourceID || ""}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a Media Source</option>
                    {mediaSources.filter((source) => source.isActive === true)
                      .length === 0 ? (
                      <option value="">No Media Sources available</option>
                    ) : (
                      mediaSources
                        .filter((source) => source.isActive === true)
                        .map((source) => (
                          <option key={source.sourceId} value={source.sourceId}>
                            {source.sourceName}
                          </option>
                        ))
                    )}
                  </select>
                )}
                {errors.mediaSourceID && (
                  <div className="invalid-feedback text-danger">
                    {errors.mediaSourceID}
                  </div>
                )}
              </div>

              <div className="col-12 col-lg-6 mb-3">
                <label htmlFor="name" className="form-label">
                  Sub Media Source Name{requiredField}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className={`form-control form-control-lg ${
                    errors.name ? "is-invalid" : ""
                  }`}
                  placeholder="Enter Sub Media Source Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
                {errors.name && (
                  <div className="invalid-feedback text-danger">
                    {errors.name}
                  </div>
                )}
              </div>

              <div className="col-12 col-lg-6 mt-5">
                <label className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleCheckboxChange}
                  />
                  <span className="form-check-label">Active</span>
                </label>
              </div>
            </div>
          </form>
        </div>

        <div
          className={`tab-pane fade ${
            activeTab === "tab2" ? "show active" : ""
          }`}
          id="tab2-tab-pane"
          role="tabpanel"
          aria-labelledby="tab2-tab"
          tabIndex={0}
        >
          <form onSubmit={handleAssignedSubmit}>
            <div className="row">
              <div className="col-12 col-lg-6 mb-3">
                <label className="form-label">
                  Assign Users{requiredField}
                </label>
                <Select
                  options={userOptions}
                  value={selectedUsers}
                  onChange={handleMultiSelectChange}
                  isMulti
                  className={`basic-multi-select ${
                    errors.listOfUsers ? "is-invalid" : ""
                  }`}
                  classNamePrefix="select"
                  placeholder="Select users to assign"
                  isLoading={isLoadingUsers || isLoadingAssignedUsers}
                  noOptionsMessage={() => "No users available"}
                />
                {errors.listOfUsers && (
                  <div className="text-danger mt-1 small">
                    {errors.listOfUsers}
                  </div>
                )}
                <div className="form-text">
                  Select multiple users to assign to this Sub Media Source
                </div>
              </div>
            </div>
          </form>
        </div>

        <div>
          {activeTab === "tab1" ? (
            <>
              <button
                type="submit"
                className="btn btn-primary me-2"
                onClick={handleSubmit}
              >
                Update
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-primary me-2"
                onClick={handleAssignedSubmit}
                disabled={isSubmittingAssignment || isLoadingAssignedUsers}
              >
                {isSubmittingAssignment || isLoadingAssignedUsers
                  ? "Saving..."
                  : "Save Assigned"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubMediaSourceEdit;
